import { ServerToClientMessage } from '@all-for-one/shared-types'
import type { Answer, Question } from '@all-for-one/shared-types'
import React, { useEffect, useRef, useState } from 'react'
import { PayAttentionPage, DoneEarlyPage, TeacherInterruptionPage, ConnectingPage } from 'components/presentation-status-page'
import { useParams, useNavigate } from 'react-router-dom'
import { createWsClient, WsClient, ConnectionState } from 'util/ws-client'
import styles from 'css/pages/student-live.module.css'
import { MultipleChoiceAnswering } from 'components/lesson-builder/question-types/multiple-choice'
import { ShortAnswerAnswering } from 'components/lesson-builder/question-types/short-answer'
import { FillBlankAnswering } from 'components/lesson-builder/question-types/fill-blank'
import { NextQuestionButton, DoneButton, BlueButton } from 'components/blue-button'
import { useAddResultMutation } from 'util/rtk-api'
import { verifyLogin } from 'util/cache'
import { normalizeSection } from '@all-for-one/shared-types'
import { getUserDetails } from 'util/api'
import { reportError } from 'util/error-reporting'

const QUESTION_COMPONENTS = {
	'multiple-choice': MultipleChoiceAnswering,
	'short-answer': ShortAnswerAnswering,
	'fill-blank': FillBlankAnswering
} as const;

// Answers are buffered here before every save attempt and cleared only after
// the server confirms, so a network blip can never silently destroy a
// student's work (M-BIG3 / err-1).
const PENDING_ANSWERS_KEY = 'afo:pending-answers'

type PendingBuffer = { studentId: string; lessonId: string; classId: string; answers: Answer[] }

const readPendingBuffer = (): PendingBuffer | null => {
	try {
		const raw = localStorage.getItem(PENDING_ANSWERS_KEY)
		return raw ? JSON.parse(raw) as PendingBuffer : null
	} catch { return null }
}

export const StudentLivePage: React.FC = () => {
	const { sessionCode } = useParams()
	const [ status, setStatus ] = useState<'connecting' | 'waiting' | 'questions' | 'interrupt' | 'done'>('connecting')
	const [ questions, setQuestions ] = useState<Question[]>([])
	const [ currentIndex, setCurrentIndex ] = useState(0)
	const [ learningStage, setLearningStage ] = useState<string | null>(null)
	const [ answers, setAnswers ] = useState<Record<string, Answer>>({})
	const [ studentId, setStudentId ] = useState<string>('')
	const [ classId, setClassId ] = useState<string>('')
	const [ lessonId, setLessonId ] = useState<string>('')
	const [ lessonName, setLessonName ] = useState<string>('')
	const [ addResult ] = useAddResultMutation()
	const navigate = useNavigate()
	const [ sessionEndedTick, setSessionEndedTick ] = useState(0)
	const [ connectionState, setConnectionState ] = useState<ConnectionState>({ status: 'connecting' })
	const [ savedStatus, setSavedStatus ] = useState<'connecting' | 'waiting' | 'questions' | 'interrupt' | 'done'>('connecting')
	const [ submitState, setSubmitState ] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
	const submitStateRef = useRef(submitState)
	submitStateRef.current = submitState

	useEffect(() => {
		verifyLogin(navigate)
	})

	useEffect(() => {
		const getStudentId = async () => {
			console.log('getting student id')
			try {
				const id = localStorage.getItem('studentId')
				if (id) {
					setStudentId(id)
				} else {
					// get the user id from the cognito id token
					const user = await getUserDetails()
					if (user) {
						setStudentId(user.userId)
					}
				}
			} catch {}
		}
		getStudentId()
	}, [])

	const StudentMessageListener = (msg: ServerToClientMessage) => {
		// Type only — payloads carry question content and student ids, and this
		// logs to the console of shared classroom devices
		console.log('StudentMessageListener', msg.type)
		switch(msg.type) {
			case 'system':
				if (msg.event === 'authenticated') {
					console.log('authenticated')
					if (!sessionCode) {
						console.error('[BUG] sessionCode is undefined! Redirecting to enter-code page.')
						navigate('/enter-code')
						return
					}
					wsClient.send({ type: 'session:join', code: sessionCode })
				}
				break
			case 'session:invalid':
				console.log('Session invalid - lesson has ended or code expired')
				navigate('/enter-code', { state: { error: 'That code is invalid or has expired. Please ask your teacher for a new code.' } })
				break
			case 'session:joined':
				console.log('session:joined')
				setStatus('waiting')
				setLessonId(msg.lessonId)
				setClassId(msg.classId)
				if (msg.lessonName) setLessonName(msg.lessonName)
				break
			case 'question:deliver':
				console.log('question:deliver')
				setQuestions(msg.payload)
				setLearningStage(msg.learningStage)
				setCurrentIndex(0)
				setStatus('questions')
				break
			case 'question:end':
				setQuestions([])
				setCurrentIndex(0)
				setLearningStage(null)
				setStatus('interrupt')
				setTimeout(() => {
					// Use callback form to get current status value (not captured value from closure)
					setStatus(currentStatus => {
						// Only switch to waiting if still on interrupt screen
						if (currentStatus === 'interrupt') {
							return 'waiting'
						}
						return currentStatus
					})
				}, 3000)
				break
			case 'session:ended':
				setSessionEndedTick(t => t + 1)
				break
		}
	}

	const wsClientRef = useRef<WsClient>(createWsClient())
    const wsClient = wsClientRef.current

	useEffect(() => {
		wsClient.onMessage(StudentMessageListener)
		wsClient.connect()

		return () => {
			wsClient.offMessage(StudentMessageListener)
			wsClient.close()
		}
	}, [sessionCode])

	// Listen for connection state changes
	useEffect(() => {
		const handleConnectionStateChange = (state: ConnectionState) => {
			setConnectionState(state)

			// When reconnecting or disconnected, show connecting screen
			if (state.status === 'reconnecting' || state.status === 'connecting') {
				// Save current status so we can restore it after reconnect
				if (status !== 'connecting') {
					setSavedStatus(status)
				}
				setStatus('connecting')
			}

			// When connected, restore previous status
			if (state.status === 'connected' && savedStatus !== 'connecting') {
				// Don't restore 'interrupt' status - it's a temporary 3-second screen
				// If we saved 'interrupt', switch to 'waiting' instead
				setStatus(savedStatus === 'interrupt' ? 'waiting' : savedStatus)
			}
		}

		wsClient.onConnectionStateChange(handleConnectionStateChange)

		return () => {
			wsClient.offConnectionStateChange(handleConnectionStateChange)
		}
	}, [status, savedStatus])

	// React to session end - navigate away, UNLESS a save is still in flight or
	// failed: navigating then would hide the retry UI while the answers are
	// only in the localStorage buffer.
	useEffect(() => {
		if (sessionEndedTick === 0) return
		if (submitStateRef.current === 'saving' || submitStateRef.current === 'error') {
			console.log('session ended but answers not saved yet - staying for retry')
			return
		}
		console.log('session ended, navigating away')
		navigate('/enter-code')
	}, [sessionEndedTick])

	/**
	 * Save answers with retries. The buffer is written BEFORE the first
	 * attempt and cleared only on server-confirmed success (err-1: the old
	 * code fired addResult with no await and showed "You're finished!"
	 * even when the request never made it).
	 */
	const submitAnswers = async (payload: PendingBuffer) => {
		setSubmitState('saving')
		try { localStorage.setItem(PENDING_ANSWERS_KEY, JSON.stringify(payload)) } catch {}
		const attempts = 3
		for (let attempt = 1; attempt <= attempts; attempt++) {
			try {
				await addResult({ ...payload, skipGrading: true }).unwrap()
				try { localStorage.removeItem(PENDING_ANSWERS_KEY) } catch {}
				setSubmitState('saved')
				return
			} catch (e) {
				if (attempt === attempts) {
					reportError('manual', e, { where: 'student-live submit', attempts: String(attempts) })
					setSubmitState('error')
					return
				}
				await new Promise(r => setTimeout(r, attempt * 1500))
			}
		}
	}

	// If a previous visit left unsaved answers for this student+lesson (tab
	// closed or refreshed mid-failure), resubmit them once we know who and
	// where we are.
	useEffect(() => {
		if (!studentId || !lessonId || submitStateRef.current !== 'idle') return
		const pending = readPendingBuffer()
		if (pending && pending.studentId === studentId && pending.lessonId === lessonId) {
			console.log('found unsaved answers from a previous attempt - resubmitting')
			setStatus('done')
			submitAnswers(pending)
		}
	}, [studentId, lessonId])

	const getAnswerKey = (stage: string | null, questionId: string) => `${stage || 'unknown'}:${questionId}`

	const ensureInitializedAnswer = (q: Question) => {
		const key = getAnswerKey(learningStage, (q as any).id)
		if (answers[key] !== undefined) return
		const idx = questions.findIndex(x => x.id === q.id)
		const questionText = Array.isArray((q as any).question)
			? (q as any).question.join(' _____ ')
			: String((q as any).question ?? '')
		setAnswers(prev => ({
			...prev,
			[key]: {
				index: idx >= 0 ? idx : currentIndex,
				questionId: (q as any).id,
				questionText,
				studentAnswer: '',
				section: normalizeSection(learningStage)
			}
		}))
	}

	const handleAnswerChange = (questionId: string, value: string | string[]) => {
		const studentAnswer = Array.isArray(value) ? value.join(' ') : String(value ?? '')
		const key = getAnswerKey(learningStage, questionId)
		setAnswers(prev => ({
			...prev,
			[key]: {
				...(prev[key] || { index: currentIndex, questionId, questionText: '', section: normalizeSection(learningStage) }),
				studentAnswer
			}
		}))
	}

	/**
	 * Build the final answer set synchronously: the current question's blank
	 * entry may not have flushed into `answers` state yet when Done is
	 * clicked, and a never-touched question must still submit as a blank
	 * (blanks are graded 0 server-side; a MISSING answer is ungradeable).
	 */
	const buildFinalAnswers = (): Answer[] => {
		const merged: Record<string, Answer> = { ...answers }
		questions.forEach((q, idx) => {
			const key = getAnswerKey(learningStage, (q as any).id)
			if (merged[key] === undefined) {
				const questionText = Array.isArray((q as any).question)
					? (q as any).question.join(' _____ ')
					: String((q as any).question ?? '')
				merged[key] = {
					index: idx,
					questionId: (q as any).id,
					questionText,
					studentAnswer: '',
					section: normalizeSection(learningStage)
				}
			}
		})
		return Object.values(merged)
	}

	const handleDone = () => {
		const finalAnswers = buildFinalAnswers()
		setStatus('done')
		if (!studentId || !lessonId || !classId) {
			// The old code silently SKIPPED the save here and told the student
			// they were finished. Missing identity is an error, not a skip.
			reportError('manual', new Error('student-live submit skipped: missing identity/session ids'), {
				hasStudentId: String(!!studentId), hasLessonId: String(!!lessonId), hasClassId: String(!!classId),
			})
			setSubmitState('error')
			return
		}
		submitAnswers({ studentId, lessonId, classId, answers: finalAnswers })
	}

	const renderDone = () => {
		switch (submitState) {
			case 'saving':
				return <ConnectingPage />
			case 'error':
				return (
					<div className={styles.questionContent} style={{ textAlign: 'center' }}>
						<h2>We couldn't save your answers</h2>
						<p style={{ fontSize: '18px' }}>Don't close this tab! Your work is still here.<br />Tell your teacher, then press Try Again.</p>
						<BlueButton label="Try Again" onClick={() => {
							const pending = readPendingBuffer()
							if (pending) {
								submitAnswers(pending)
							} else if (studentId && lessonId && classId) {
								submitAnswers({ studentId, lessonId, classId, answers: buildFinalAnswers() })
							}
						}} />
					</div>
				)
			default:
				return <DoneEarlyPage />
		}
	}

	const renderContent = () => {
		// Terminal connection states get honest messages instead of the old
		// infinite "Connecting..." spinner (WS-5/WS-7/err-6)
		if (connectionState.closeReason === 'evicted') {
			return (
				<div className={styles.questionContent} style={{ textAlign: 'center' }}>
					<h2>This lesson is open somewhere else</h2>
					<p style={{ fontSize: '18px' }}>You joined from another tab or device, so this one stopped.<br />Close this tab and keep working there.</p>
				</div>
			)
		}
		if (connectionState.status === 'failed' || connectionState.closeReason === 'auth') {
			return (
				<div className={styles.questionContent} style={{ textAlign: 'center' }}>
					<h2>We can't reach the classroom</h2>
					<p style={{ fontSize: '18px' }}>Check your internet, then refresh this page.<br />If it keeps happening, tell your teacher.</p>
					<BlueButton label="Refresh" onClick={() => window.location.reload()} />
				</div>
			)
		}
		switch (status) {
			case 'connecting':
				return <ConnectingPage
					isReconnecting={connectionState.status === 'reconnecting'}
					reconnectAttempt={connectionState.reconnectAttempt}
					maxAttempts={10}
				/>
			case 'waiting':
				return <PayAttentionPage />
			case 'interrupt':
				return <TeacherInterruptionPage />
			case 'done':
				return renderDone()
			case 'questions': {
				const q = questions[currentIndex]
				if (!q) return <PayAttentionPage />
				const QuestionComponent = QUESTION_COMPONENTS[q.type] as any
				// err-13: an AI-generated question with an unknown type used to
				// crash the whole render tree. Show a fallback and let the
				// student keep moving; the blank answer grades as 0.
				if (!QuestionComponent) {
					reportError('manual', new Error(`unknown question type: ${String((q as any).type)}`), { where: 'student-live render' })
					return (
						<div className={styles.questionContent} style={{ textAlign: 'center' }}>
							<h2>This question can't be shown</h2>
							<p style={{ fontSize: '18px' }}>Skip it and keep going — tell your teacher about this one.</p>
							<div className={styles.questionActions}>
								{currentIndex < questions.length - 1
									? <NextQuestionButton onClick={() => setCurrentIndex(currentIndex + 1)} />
									: <DoneButton onClick={handleDone} />}
							</div>
						</div>
					)
				}
				const currentAnswer = answers[getAnswerKey(learningStage, (q as any).id)]
				let answerValue: any = undefined
				if (q.type === 'fill-blank') {
					const expected = (q as any).question.length - 1
					if (currentAnswer?.studentAnswer) {
						const parts = String(currentAnswer.studentAnswer).split(' ')
						answerValue = Array.from({ length: expected }, (_, i) => parts[i] || '')
					}
				} else {
					answerValue = currentAnswer?.studentAnswer ?? ''
				}
				ensureInitializedAnswer(q)
				return (
					<div className={styles.questionContent}>
						{learningStage ? (<h2>{`${learningStage} Questions`}</h2>) : null}
						<QuestionComponent 
							key={q.id} 
							question={q} 
							answer={answerValue}
							onAnswerChange={(val: any) => handleAnswerChange(q.id, val)}
						/>
						<div className={styles.questionActions}>
							{currentIndex < questions.length - 1 ? (
								<NextQuestionButton
									onClick={() => {
										const curr = questions[currentIndex]
										ensureInitializedAnswer(curr)
										if (currentIndex < questions.length - 1) {
											setCurrentIndex(currentIndex + 1)
										}
									}}
								/>
							) : (
								<DoneButton onClick={handleDone} />
							)}
						</div>
					</div>
				)
			}
			default:
				return null
		}
	}

	return (
		<div className={styles.studentLiveContainer}>
			{renderContent()}
		</div>
	)
}


